import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon as IconifyIcon, loadIcon } from '@iconify/react';
import css from './Icon.css';
import Tooltip from '../molecules/Tooltip';

export const IconTypes = {
    TABLER: 'tabler',
    MINGCUTE: 'mingcute',
};

export const IconVariants = {
    DARK: 'dark',
    LIGHT: 'light',
    WHITE: 'white',
    ACTIVE: 'active',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
    INDIGO: 'indigo',
};

export const IconSizes = {
    X_SMALL: 'x-small',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    X_LARGE: 'x-large',
};

const Icon = ({
    name,
    type,
    size = IconSizes.LARGE,
    variant = IconVariants.DARK,
    disabled = false,
    className = '',
    onClick,
    interactive = true,
    hoverText,
    ...props
}) => {
    const [iconName, setIconName] = useState(null);

    useEffect(() => {
        if (type) {
            setIconName(`${type}:${name}`);
            return;
        }

        let isMounted = true;

        // if the type isn't specified, the icon type will be resolved dynamically.
        const resolveIconName = async () => {
            const mainIcon = `${IconTypes.TABLER}:${name}`;
            const fallbackIcon = `${IconTypes.MINGCUTE}:${name}`;
            let icon;

            try {
                await loadIcon(mainIcon);
                icon = mainIcon;
            } catch {
                try {
                    await loadIcon(fallbackIcon);
                    icon = fallbackIcon;
                } catch {
                    icon = null;
                }
            }

            isMounted && setIconName(icon);
        };

        resolveIconName();

        return () => {
            isMounted = false;
        };
    }, [name, type]);

    const classes = classNames(css.singularIcon, css[size], css[variant], className, {
        [css.disabled]: disabled,
        [css.interactive]: interactive,
    });

    const IconElement = <IconifyIcon icon={iconName} className={classes} onClick={onClick} {...props} />;

    if (!hoverText) {
        return IconElement;
    }

    return (
        <Tooltip wrapperClass={css.tooltip} titleTranslationKey={hoverText}>
            {IconElement}
        </Tooltip>
    );
};

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.values(IconTypes)),
    size: PropTypes.oneOf(Object.values(IconSizes)),
    variant: PropTypes.oneOf(Object.values(IconVariants)),
    disabled: PropTypes.bool,
    interactive: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
    hoverText: PropTypes.string,
};

export default Icon;
